import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, MapPin, QrCode, Store, Utensils, Package } from 'lucide-react';
export const Route = createFileRoute('/_layout/learn/qr-scan-tracking/')({
    component: LearnQrScanTrackingPage,
    head: () => ({
        meta: [
            { title: 'QR Scan Tracking — What Happens After Someone Scans | Adora' },
            {
                name: 'description',
                content: 'QR scan tracking shows how people interact with menus, flyers, and packaging after scanning. Learn how to turn every scan into campaign data with Adora.',
            },
            { property: 'og:title', content: 'QR Scan Tracking — What Happens After Someone Scans | Adora' },
            {
                property: 'og:description',
                content: 'QR scan tracking shows how people interact with menus, flyers, and packaging after scanning. Learn how to turn every scan into campaign data with Adora.',
            },
        ],
    }),
});
function LearnQrScanTrackingPage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* HERO */}
            <section className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground">
                    <QrCode className="h-3.5 w-3.5"/>
                    Learn · QR Scan Tracking
                </div>

                <h1 className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">
                    Track what happens after someone scans a QR code
                </h1>

                <p className="text-muted-foreground max-w-2xl">
                    QR scan tracking shows how people interact with your physical materials — menus, flyers, packaging,
                    posters — once they scan.
                </p>

                <Button asChild size="lg">
                    <Link to="/sign-up" search={{ redirect: '/console' }}>
                        Try QR tracking for free
                    </Link>
                </Button>
            </section>

            <Separator className="my-12"/>

            {/* WHAT IT MEANS */}
            <section className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <QrCode className="h-5 w-5 text-primary"/>
                            What QR scan tracking means
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                        <p>QR scan tracking connects a physical QR code to real data — not guesses.</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Each scan is counted</li>
                            <li>Each QR belongs to a campaign</li>
                            <li>Each placement can be compared</li>
                        </ul>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-primary"/>
                            What you can see
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Total scans</li>
                            <li>Unique visitors</li>
                            <li>Scan trends over time</li>
                            <li>Which QR performs better</li>
                        </ul>
                    </CardContent>
                </Card>
            </section>

            <Separator className="my-12"/>

            {/* REAL-WORLD USE CASES */}
            <section className="space-y-6">
                <h2 className="text-2xl font-semibold">Where QR scan tracking works best</h2>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <UseCase icon={<Utensils className="h-5 w-5"/>} title="Restaurants & cafés" text="Menus, table stickers, takeaway packaging"/>
                    <UseCase icon={<Store className="h-5 w-5"/>} title="Retail stores" text="In-store posters, shelves, windows"/>
                    <UseCase icon={<Package className="h-5 w-5"/>} title="Packaged products" text="Labels, boxes, bottles, bags"/>
                    <UseCase icon={<MapPin className="h-5 w-5"/>} title="Events & street campaigns" text="Flyers, banners, temporary signage"/>
                </div>
            </section>

            <Separator className="my-12"/>

            {/* HOW IT FITS ADORA */}
            <section className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>QR tracking with Adora</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>Adora treats each QR code as part of a campaign.</p>
                        <p>You can compare placements, designs, or locations over time.</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Not just scans</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>QR scans are the entry point.</p>
                        <p>Adora focuses on understanding which physical ideas actually work.</p>
                    </CardContent>
                </Card>
            </section>

            <Separator className="my-12"/>

            {/* CTA */}
            <Card>
                <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <h3 className="text-xl font-semibold">Turn QR codes into measurable campaigns</h3>
                        <p className="text-sm text-muted-foreground max-w-xl mt-1">
                            Create QR codes, track scans, and understand which placements actually perform.
                        </p>
                    </div>

                    <div className="flex gap-3">
                        <Button asChild>
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Start free
                            </Link>
                        </Button>
                        <Button asChild variant="outline">
                            <Link to="/pricing">View pricing</Link>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </main>);
}
function UseCase({ icon, title, text }) {
    return (<Card className="h-full">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {icon}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{text}</CardContent>
        </Card>);
}
